import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import FilterBar from '../components/FilterBar';
import api from '../api';
import { useAuth } from '../AuthContext';

const TOBACCO_LABELS = { '-1': 'Never', '0': 'Current', '1': '1yr ago', '2': '2yrs ago', '3': '3yrs ago', '5': '5+ yrs ago' };

function fmt(amount, dec = 2) {
  if (!amount) return '';
  return '$' + parseFloat(amount).toLocaleString('en-US', { maximumFractionDigits: dec, minimumFractionDigits: dec });
}
function fmtDate(s, tz = 'America/Chicago') {
  if (!s) return '';
  return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
}
function fmtTime(s, tz = 'America/Chicago') {
  if (!s) return '';
  return new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: tz });
}

function QuoteDetail({ quote, engines, coverageTypes }) {
  const d = quote.details || {};
  const termLabel = (coverageTypes?.[quote.engine_id] || []).find(t => t.value === d.category_code)?.title || '';

  const Field = ({ label, value }) => value ? (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#0F172A', fontWeight: '500' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{ background: '#F8FAFC', padding: '20px 24px', borderTop: '2px solid #2D87F0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>

        {/* Contact */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-user"></i> Contact
          </div>
          <Field label="Name" value={quote.contact_full_name} />
          <Field label="Email" value={quote.contact_email} />
          <Field label="Gender" value={d.gender} />
          <Field label="Date of Birth" value={d.dob_year ? `${d.dob_month}/${d.dob_day}/${d.dob_year}` : ''} />
          <Field label="State" value={d.state_code} />
        </div>

        {/* Quote */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-file-invoice-dollar"></i> Quote Details
          </div>
          <Field label="Engine" value={d.engine_name || engines?.[quote.engine_id]} />
          <Field label="Coverage" value={d.coverage_amount ? fmt(d.coverage_amount, 0) : ''} />
          <Field label="Term / Type" value={termLabel} />
          <Field label="Monthly" value={d.monthly_premium ? fmt(d.monthly_premium) + '/mo' : ''} />
          <Field label="Annual" value={d.annual_premium ? fmt(d.annual_premium, 0) + '/yr' : ''} />
        </div>

        {/* Health */}
        <div>
          <div style={{ fontWeight: '700', fontSize: '12px', color: '#2D87F0', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fas fa-heart-pulse"></i> Health Profile
          </div>
          <Field label="Height" value={d.height} />
          <Field label="Weight" value={d.weight ? `${d.weight} lbs` : ''} />
          <Field label="Health Class" value={d.health_class_code} />
          <Field label="Nicotine" value={TOBACCO_LABELS[String(d.tobacco_years_ago)]} />
          {d.quote_url && (
            <div style={{ marginTop: '12px' }}>
              <a href={d.quote_url} target="_blank" rel="noreferrer" className="btn-primary" style={{ fontSize: '12px', padding: '6px 14px', textDecoration: 'none', height: '30px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <i className="fas fa-external-link-alt"></i> View Results
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function QuotesPage() {
  const { brand } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [contact, setContact] = useState('');
  const [engineFilter, setEngineFilter] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [engines, setEngines] = useState({});
  const [coverageTypes, setCoverageTypes] = useState({});

  const fetchQuotes = async (p = 1, filters = {}, ps = pageSize) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: ps };
      const s  = filters.start       ?? startDate;
      const e  = filters.end         ?? endDate;
      const c  = filters.contact     ?? contact;
      const ef = filters.engineFilter ?? engineFilter;
      const mn = filters.minAmount   ?? minAmount;
      const mx = filters.maxAmount   ?? maxAmount;
      if (s)  params.start = s;
      if (e)  params.end = e;
      if (c)  params.term = c;
      if (ef) params.engine_id = ef;
      if (mn) params.min_amount = mn;
      if (mx) params.max_amount = mx;
      const res = await api.get('/quotes', { params });
      setQuotes(res.data.quotes);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setPage(p);
      setEngines(res.data.engines || {});
      setCoverageTypes(res.data.coverage_types || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const handleReset = () => {
    setStartDate(''); setEndDate(''); setContact('');
    setEngineFilter(''); setMinAmount(''); setMaxAmount('');
    fetchQuotes(1, { start: '', end: '', contact: '', engineFilter: '', minAmount: '', maxAmount: '' });
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>

        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', margin: '0 0 16px' }}>All Quotes</h1>

        <FilterBar
          startDate={startDate} endDate={endDate}
          onDateChange={(s, e) => { setStartDate(s); setEndDate(e); fetchQuotes(1, { start: s, end: e }); }}
          contact={contact} onContactChange={(v) => { setContact(v); fetchQuotes(1, { contact: v }); }}
          typeOptions={Object.entries(engines).map(([id, title]) => ({ value: id, label: title }))}
          selectedType={engineFilter} onTypeChange={(v) => { setEngineFilter(v); fetchQuotes(1, { engineFilter: v }); }}
          typePlaceholder="All quote types"
          minAmount={minAmount} maxAmount={maxAmount}
          onAmountChange={(mn, mx) => { setMinAmount(mn); setMaxAmount(mx); fetchQuotes(1, { minAmount: mn, maxAmount: mx }); }}
          onReset={handleReset}
        />

        <div className="portal-card" style={{ overflow: 'hidden' }}>

          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748B' }}>
              {loading ? 'Loading...' : total === 0 ? 'No quotes found' : `Showing ${from}–${to} of ${total.toLocaleString()} quotes`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#64748B' }}>Per page:</span>
                <select value={pageSize} onChange={e => { const ps = parseInt(e.target.value); setPageSize(ps); fetchQuotes(1, {}, ps); }}
                  style={{ height: '28px', padding: '0 20px 0 8px', border: '1px solid #DDE7F1', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', appearance: 'none', background: 'white' }}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => fetchQuotes(page - 1)} disabled={page === 1}
                    style={{ width: '28px', height: '28px', border: '1px solid #DDE7F1', borderRadius: '6px', background: 'white', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, fontSize: '12px' }}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let p;
                    if (totalPages <= 7) p = i + 1;
                    else if (page <= 4) p = i + 1;
                    else if (page >= totalPages - 3) p = totalPages - 6 + i;
                    else p = page - 3 + i;
                    return (
                      <button key={p} onClick={() => fetchQuotes(p)}
                        style={{ width: '28px', height: '28px', border: `1px solid ${p === page ? '#2D87F0' : '#DDE7F1'}`, borderRadius: '6px', background: p === page ? '#2D87F0' : 'white', color: p === page ? 'white' : '#334155', cursor: 'pointer', fontSize: '12px', fontWeight: p === page ? '600' : '400' }}>
                        {p}
                      </button>
                    );
                  })}
                  <button onClick={() => fetchQuotes(page + 1)} disabled={page === totalPages}
                    style={{ width: '28px', height: '28px', border: '1px solid #DDE7F1', borderRadius: '6px', background: 'white', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, fontSize: '12px' }}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '20px' }}>
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: '52px', borderRadius: '6px', marginBottom: '8px' }}></div>)}
            </div>
          ) : quotes.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>
              <i className="fas fa-file-invoice" style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}></i>
              No quotes found
            </div>
          ) : (
            <table data-testid="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Date', 'Engine', 'Contact', 'Coverage', 'Premium', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDE7F1', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quotes.map(quote => {
                  const isExpanded = expandedId === quote.id;
                  const d = quote.details || {};
                  return (
                    <React.Fragment key={quote.id}>
                      <tr
                        onClick={() => setExpandedId(prev => prev === quote.id ? null : quote.id)}
                        style={{ borderBottom: isExpanded ? 'none' : '1px solid #F1F5F9', cursor: 'pointer', background: isExpanded ? '#EFF6FF' : 'white', transition: 'background 150ms' }}
                        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#FAFBFF'; }}
                        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'white'; }}
                      >
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>{fmtDate(quote.created_at, brand?.timezone)}</div>
                          <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '1px' }}>{fmtTime(quote.created_at, brand?.timezone)}</div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ fontSize: '12px', background: '#EFF6FF', color: '#2D87F0', borderRadius: '6px', padding: '3px 8px', fontWeight: '500', border: '1px solid #BFDBFE' }}>
                            {d.engine_name || engines?.[quote.engine_id] || 'Unknown'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>{quote.contact_full_name || '—'}</div>
                          {quote.contact_email && <div style={{ fontSize: '12px', color: '#64748B', marginTop: '1px' }}>{quote.contact_email}</div>}
                        </td>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>{d.coverage_amount ? fmt(d.coverage_amount, 0) : '—'}</div>
                          {(coverageTypes?.[quote.engine_id] || []).find(t => t.value === d.category_code)?.title && (
                            <div style={{ fontSize: '11px', color: '#64748B', marginTop: '1px' }}>{(coverageTypes?.[quote.engine_id] || []).find(t => t.value === d.category_code)?.title}</div>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          {d.monthly_premium ? (
                            <>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>{fmt(d.monthly_premium)}<span style={{ fontWeight: '400', color: '#64748B' }}>/mo</span></div>
                              {d.annual_premium && <div style={{ fontSize: '11px', color: '#64748B', marginTop: '1px' }}>{fmt(d.annual_premium, 0)}/yr</div>}
                            </>
                          ) : <span style={{ color: '#94A3B8', fontSize: '13px' }}>—</span>}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <button data-testid="data-table-row-expand-button"
                            onClick={e => { e.stopPropagation(); setExpandedId(prev => prev === quote.id ? null : quote.id); }}
                            style={{ width: '30px', height: '30px', borderRadius: '7px', border: `1px solid ${isExpanded ? '#2D87F0' : '#DDE7F1'}`, background: isExpanded ? '#2D87F0' : 'white', color: isExpanded ? 'white' : '#64748B', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', transition: 'all 150ms' }}>
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0 }} data-testid="data-table-row-expanded-panel">
                            <QuoteDetail quote={quote} engines={engines} coverageTypes={coverageTypes} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </ProtectedLayout>
  );
}
